import { yupResolver } from "@hookform/resolvers/yup";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { ConstructionForm } from "~/components/common/constructions/ConstructionForm";
import type { NskResponse } from "~/models/Api";
import type { DealConstructionEdit } from "~/models/DealConstruction";
import {
	buildDefaultConstruction,
	validationSchemaConstruction
} from "~/models/DealConstruction";
import type { DealConstructionItem } from "~/models/DealConstructionItem";
import { isSuccess } from "~/utils/api/api_handler";
import { ConstructionFormFooter } from "../ConstructionFormFooter";

type Props = {
	dealConstructionItems: DealConstructionItem[];
	disabled: boolean;
	handleClose: () => void;
	handleCreate: (submitValue: DealConstructionEdit) => Promise<NskResponse>;
	mutateConstructions: () => Promise<void>;
};

export function ConstructionAddModal({
	handleClose,
	handleCreate,
	dealConstructionItems,
	mutateConstructions,
	disabled
}: Props) {
	const { notify } = useNotification();
	const methods = useForm<DealConstructionEdit>({
		defaultValues: buildDefaultConstruction("add"),
		mode: "onBlur",
		resolver: yupResolver(validationSchemaConstruction)
	});
	const onClickSubmit = useCallback(
		(reInput: boolean) => {
			void methods.handleSubmit(async (data) => {
				const response = await handleCreate(data);
				if (isSuccess(response)) {
					await mutateConstructions();
					methods.reset();
					notify({ content: "追加しました。", variant: "success" });
					if (!reInput) {
						handleClose();
					}
					return;
				}
				notify({ content: "エラーが発生しました。", variant: "error" });
				methods.reset();
			})();
		},
		[handleClose, handleCreate, methods, mutateConstructions, notify]
	);
	return (
		<Modalv2 isOpen onClose={handleClose}>
			<ModalHeader title="見積り追加" />
			<ModalBody>
				<FormProvider {...methods}>
					<FormGroup variant="vertical">
						<ConstructionForm
							dealConstructionItems={dealConstructionItems}
							disabled={disabled}
							docType="estimation"
						/>
					</FormGroup>
					<ConstructionFormFooter
						disabled={disabled || methods.formState.isSubmitting}
						onClickSubmit={onClickSubmit}
						type="add_modal"
					/>
				</FormProvider>
			</ModalBody>
		</Modalv2>
	);
}
